import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  TextField,
  Button,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Autocomplete,
  alpha,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createEnquiry,
  updateEnquiry,
  ApprovePartyList,
  getVoltageRatio,
  getRatingSpecifications,
  getWindingMaterials,
} from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditNoteIcon from "@mui/icons-material/EditNote";

export default function EnquiryForm() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const location = useLocation();

  // ── Theme-aware helpers ──
  const cardSx = {
    mb: 3,
    borderRadius: 2,
    bgcolor: "background.paper",
    backgroundImage: "none",
    border: "1px solid",
    borderColor: "divider",
    boxShadow: isDark ? "0 4px 18px rgba(0,0,0,0.35)" : 2,
  };

  const sectionHeadingSx = {
    color: "primary.main",
    fontWeight: "bold",
    display: "flex",
    alignItems: "center",
    gap: 0.5,
  };

  const tablePaperSx = {
    boxShadow: 0,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: "background.paper",
    backgroundImage: "none",
  };

  const tableHeadBg = isDark
    ? alpha(theme.palette.primary.main, 0.15)
    : "#f8f9fa";

  const noteBg = isDark ? alpha(theme.palette.common.white, 0.04) : "#fafafa";

  // Constants
  const initialLineItem = {
    voltage_ratio: "",
    rating_specification: "",
    winding_material: "",
    quantity: "1",
    price_per_unit: "",
    description: "",
  };
  const initialNote = { note_text: "" };

  const API_BASE_URL = "https://api.countrystatecity.in/v1";
  const API_KEY =
    "dc87a0d6844b70cf9f1294bdfe6d00cdfa09ebc7adf7b26d3420a0fe1bd10491";

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
    } catch {
      return "";
    }
  };

  const [enquiryData] = useState(() => {
    const data = location.state?.data ?? null;
    return data;
  });

  const isEditMode = !!enquiryData;

  const [partyOptions, setPartyOptions] = useState([]);
  const [partyLoading, setPartyLoading] = useState(false);
  const [partySearch, setPartySearch] = useState("");

  const [voltageOptions, setVoltageOptions] = useState([]);
  const [ratingOptions, setRatingOptions] = useState([]);
  const [windingOptions, setWindingOptions] = useState([]);

  const [formData, setFormData] = useState(() => ({
    company_name: enquiryData?.company_name || "",
    customer_name:
      enquiryData?.customer_name || enquiryData?.contact_person || "",
    email: enquiryData?.email || "",
    phone: enquiryData?.phone || "",
    address: enquiryData?.address || "",
    city: enquiryData?.city || "",
    state: enquiryData?.state || "",
    country: enquiryData?.country || "India",
    enquiry_source: enquiryData?.enquiry_source || enquiryData?.enq_type || "",
    // enq_type: enquiryData?.enq_type || "",
    follow_up_date: formatDateForInput(enquiryData?.follow_up_date),
    status: enquiryData?.status || "New",
    division: null, // Always null to prevent Invalid PK Error
  }));

  const [lineItems, setLineItems] = useState(() => {
    if (enquiryData?.line_items?.length > 0) {
      return enquiryData.line_items.map((item) => ({
        voltage_ratio: item.voltage_ratio || "",
        rating_specification:
          item.rating_specification || item.description || "",
        winding_material: item.winding_material || "",
        quantity: item.quantity?.toString() || "1",
        price_per_unit: item.price_per_unit?.toString() || "",
        description: item.description || "",
      }));
    }
    return [{ ...initialLineItem }];
  });

  const [notes, setNotes] = useState(() => {
    if (enquiryData?.notes?.length > 0) {
      return enquiryData.notes.map((note) => ({
        note_text: note.note_text || "",
      }));
    }
    return [{ ...initialNote }];
  });

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [voltRes, rateRes, windRes] = await Promise.all([
          getVoltageRatio(1, ""),
          getRatingSpecifications(1, ""),
          getWindingMaterials(1, ""),
        ]);
        setVoltageOptions(voltRes.results || []);
        setRatingOptions(rateRes.results || []);
        setWindingOptions(windRes.results || []);
      } catch (err) {
        console.error("Error fetching masters", err);
      }
    };
    fetchMasters();
  }, []);

  const [errors, setErrors] = useState({ email: "", phone: "" });
  const [loading, setLoading] = useState(false);

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  const getParties = async (filters = {}) => {
    try {
      setPartyLoading(true);
      const response = await ApprovePartyList(1, filters);
      const results = Array.isArray(response) ? response : [];
      setPartyOptions(results);
    } catch (err) {
      console.error("Error fetching parties:", err);
      toast.error("Failed to load debtors");
    } finally {
      setPartyLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      getParties({ name: partySearch });
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [partySearch]);

  const fetchStatesForIndia = async () => {
    setLoadingStates(true);
    try {
      const response = await fetch(`${API_BASE_URL}/countries/IN/states`, {
        headers: { "X-CSCAPI-KEY": API_KEY },
      });
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setStates(data);
      } else {
        toast.error("No states found");
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      toast.error("Failed to load states.");
      setStates([]);
    } finally {
      setLoadingStates(false);
    }
  };

  const fetchCitiesForState = async (stateCode) => {
    setLoadingCities(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/countries/IN/states/${stateCode}/cities`,
        { headers: { "X-CSCAPI-KEY": API_KEY } },
      );
      if (!response.ok)
        throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setCities(data);
      } else {
        toast.warning("No cities found for this state");
        setCities([]);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
      toast.error("Failed to load cities");
      setCities([]);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    fetchStatesForIndia();
  }, []);

  useEffect(() => {
  if (formData.state && states.length > 0 && cities.length === 0) {
    const stateObj = states.find((s) => s.name === formData.state);
    if (stateObj) {
      fetchCitiesForState(stateObj.iso2);
    }
  }
}, [formData.state, states]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone" && !/^\d*$/.test(value)) return;
    if (name === "state" || name === "city") return;

    setFormData((prev) => ({ ...prev, [name]: value }));

    let errorMsg = "";
    if (name === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (value && !emailRegex.test(value))
        errorMsg = "Invalid email format (e.g., abc@gmail.com)";
    }
    if (name === "phone") {
      if (value.length === 10) {
        const phoneRegex = /^[6-9]\d{9}$/;
        if (!phoneRegex.test(value))
          errorMsg = "Phone number must start with 6-9";
      } else if (value.length > 10) {
        errorMsg = "Phone number can't exceed 10 digits";
      } else if (value.length > 0 && value.length < 10) {
        errorMsg = "Phone number must be 10 digits";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleLineItemChange = (index, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[index][field] = value;
      return updated;
    });
  };

  const handleNoteChange = (index, value) => {
    setNotes((prev) => {
      const updated = [...prev];
      updated[index].note_text = value;
      return updated;
    });
  };

  const addLineItem = () => {
    if (lineItems.length >= 20) {
      toast.warning("Maximum 20 line items allowed!");
      return;
    }
    setLineItems((prev) => [...prev, { ...initialLineItem }]);
  };

  const addNote = () => {
    if (notes.length >= 10) {
      toast.warning("Maximum 10 notes allowed!");
      return;
    }
    setNotes((prev) => [...prev, { ...initialNote }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length === 1) {
      toast.error("At least one line item is required!");
      return;
    }
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNote = (index) => {
    if (notes.length === 1) {
      setNotes([{ note_text: "" }]);
      return;
    }
    setNotes((prev) => prev.filter((_, i) => i !== index));
  };

  const validateFields = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[6-9]\d{9}$/;
    const newErrors = {
  email:
    formData.email && !emailRegex.test(formData.email)
      ? "Invalid email format"
      : "",
  phone:
    formData.phone && !phoneRegex.test(formData.phone)
      ? "Enter valid 10-digit phone"
      : "",
};
    setErrors(newErrors);

    let lineItemsValid = true;
    lineItems.forEach((item, index) => {
      const hasVoltage =
        item.voltage_ratio && item.voltage_ratio.toString().trim() !== "";
      const hasRating =
        item.rating_specification &&
        item.rating_specification.toString().trim() !== "";
      const hasWinding =
        item.winding_material && item.winding_material.toString().trim() !== "";
      const hasQty = item.quantity && item.quantity.toString().trim() !== "";

      if (!hasVoltage || !hasRating || !hasWinding || !hasQty) {
        lineItemsValid = false;
        toast.error(
          `Please fill Voltage, Rating, Winding & Qty in line item ${index + 1}`,
        );
      }
    });

    const hasNotes = notes.some((note) => note.note_text.trim() !== "");
    if (!hasNotes) toast.error("Please add at least one note");

    if (!formData.state) {
      toast.error("Please select a state");
      return false;
    }
    if (!formData.city) {
      toast.error("Please select a city");
      return false;
    }

    return !newErrors.email && !newErrors.phone && lineItemsValid && hasNotes;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const userId = localStorage.getItem("user_id");
    const parsedUserId = userId ? parseInt(userId, 10) : null;

    const filteredNotes = notes.filter((note) => note.note_text.trim() !== "");

    const cleanedFollowUpDate =
      formData.follow_up_date && formData.follow_up_date.trim() !== ""
        ? formData.follow_up_date
        : null;

    const cleanedLineItems = lineItems.map((item) => ({
      voltage_ratio: item.voltage_ratio || "",
      rating_specification: item.rating_specification || "",
      winding_material: item.winding_material || "",
      quantity: parseFloat(item.quantity) || 1,
      price_per_unit: item.price_per_unit ? parseFloat(item.price_per_unit) : 0,
      description: item.rating_specification || item.description || "",
      unit: item.winding_material || "nos",
    }));

    const dataToSend = {
      ...formData,
      enq_type: formData.enquiry_source,
      division: null, // Always send null to prevent Foreign Key Error
      created_by: parsedUserId,
      follow_up_date: cleanedFollowUpDate,
      line_items: cleanedLineItems,
      notes: filteredNotes,
    };

    setLoading(true);
    try {
      if (isEditMode) {
        await updateEnquiry(enquiryData.enquiry_id, dataToSend);
        toast.success("Enquiry updated successfully!");
      } else {
        await createEnquiry(dataToSend);
        toast.success("Enquiry submitted successfully!");
      }
      setTimeout(() => navigate("/dashboard/Enquiry"), 1500);
    } catch (error) {
      console.error("Submission Error Details:", error);

      const errorData = error.response?.data;
      if (errorData && typeof errorData === "object") {
        const errorMessages = Object.entries(errorData)
          .map(
            ([field, msgs]) =>
              `${field}: ${Array.isArray(msgs) ? msgs.join(", ") : msgs}`,
          )
          .join(" | ");
        toast.error(`Backend Error: ${errorMessages}`);
      } else {
        toast.error(
          isEditMode
            ? "Failed to update enquiry!"
            : "Failed to submit enquiry!",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" disableGutters sx={{ mt: 0, pt: 0, px: 0 }}>
      <ToastContainer theme={isDark ? "dark" : "light"} />

      <form onSubmit={handleSubmit} autoComplete="off">
        {/* ── Top Action Bar ── */}
        {isMobile ? (
          <Grid
            container
            spacing={2}
            sx={{
              mb: 2,
              mt: { xs: 4, sm: 0, md: -3 },
              position: "sticky",
              top: 0,
              zIndex: 1100,
              pt: 1,
              pb: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
              backdropFilter: "blur(8px)",
            }}
          >
            <Grid item xs={6}>
              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ height: 40, borderRadius: 0, fontSize: "0.75rem" }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isEditMode ? (
                  "Update"
                ) : (
                  "Submit"
                )}
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate("/dashboard/Enquiry")}
                sx={{ height: 40, borderRadius: 0, fontSize: "0.75rem" }}
              >
                ← Back
              </Button>
            </Grid>
          </Grid>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              mt: -3,
              position: "sticky",
              top: 0,
              zIndex: 1100,
              pt: 1,
              pb: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: (t) => alpha(t.palette.background.paper, 0.95),
              backdropFilter: "blur(8px)",
              boxShadow: isDark
                ? "0 2px 10px rgba(0,0,0,0.45)"
                : "0 1px 4px rgba(0,0,0,0.07)",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", color: "text.primary" }}
            >
              {isEditMode ? "Edit Enquiry" : "Create Enquiry"}
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                sx={{ borderRadius: 0 }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isEditMode ? (
                  "Update Enquiry"
                ) : (
                  "Submit Enquiry"
                )}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate("/dashboard/Enquiry")}
                sx={{ borderRadius: 0 }}
              >
                ← Back
              </Button>
            </Box>
          </Box>
        )}

        {/* ── Customer Information ── */}
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 2 }}>
              <EditNoteIcon sx={{ fontSize: "1.2rem" }} />
              Customer Information
            </Typography>

            <Grid container spacing={2}>
              {/* Company Autocomplete */}
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={partyOptions}
                  getOptionLabel={(option) =>
                    option.company_name ||
                    option.customer_name ||
                    option.name ||
                    ""
                  }
                  value={
                    partyOptions.find(
                      (option) =>
                        (option.company_name ||
                          option.customer_name ||
                          option.name) === formData.company_name,
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const phoneValue =
                        newValue.phone ||
                        newValue.mobile ||
                        newValue.phone_no ||
                        newValue.contact ||
                        "";
                      setFormData((prev) => ({
                        ...prev,
                        company_name:
                          newValue.company_name ||
                          newValue.customer_name ||
                          newValue.name ||
                          "",
                        customer_name:
                          newValue.contact_person ||
                          newValue.contact_name ||
                          newValue.customer_name ||
                          newValue.name ||
                          "",
                        email: newValue.email || "",
                        phone: phoneValue.replace(/\D/g, ""),
                        address: newValue.address || "",
                      }));
                    } else {
                      setFormData((prev) => ({
                        ...prev,
                        company_name: "",
                        customer_name: "",
                        email: "",
                        phone: "",
                        address: "",
                      }));
                    }
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === "input") setPartySearch(newInputValue);
                  }}
                  loading={partyLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Company Name"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {partyLoading && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.company_name ||
                        option.customer_name ||
                        option.name ||
                        "Unnamed"}
                    </li>
                  )}
                  noOptionsText="No companies found"
                  filterOptions={(options, state) => {
                    const searchTerm = state.inputValue.toLowerCase().trim();
                    if (!searchTerm) return options;
                    return options.filter((option) =>
                      (
                        option.company_name ||
                        option.customer_name ||
                        option.name ||
                        ""
                      )
                        .toLowerCase()
                        .includes(searchTerm),
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    (option.company_name ||
                      option.customer_name ||
                      option.name) ===
                    (value.company_name || value.customer_name || value.name)
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>

              {/* Contact Person */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  name="customer_name"
                  value={formData.customer_name}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  size="small"
                />
              </Grid>

              {/* Phone */}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  inputProps={{ maxLength: 10 }}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  required
                  size="small"
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} sm={2}>
                <Autocomplete
                  options={states}
                  getOptionLabel={(option) => option.name || ""}
                  value={states.find((s) => s.name === formData.state) || null}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      state: newValue?.name || "",
                      city: "",
                    }));
                    if (newValue) fetchCitiesForState(newValue.iso2);
                  }}
                  loading={loadingStates}
                  disabled={loadingStates}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStates && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.iso2}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText="No states found"
                  filterOptions={(options, state) => {
                    const searchTerm = state.inputValue.toLowerCase().trim();
                    if (!searchTerm) return options;
                    return options.filter((option) =>
                      option.name.toLowerCase().includes(searchTerm),
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.name === value?.name
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>

              {/* City */}
              <Grid item xs={12} sm={2}>
                <Autocomplete
                  options={cities}
                  getOptionLabel={(option) => option.name || ""}
                  value={cities.find((c) => c.name === formData.city) || null}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({
                      ...prev,
                      city: newValue?.name || "",
                    }));
                  }}
                  loading={loadingCities}
                  disabled={!formData.state || loadingCities}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="City"
                      required
                      size="small"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingCities && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      {option.name}
                    </li>
                  )}
                  noOptionsText={
                    formData.state ? "No cities found" : "Select State First"
                  }
                  filterOptions={(options, state) => {
                    const searchTerm = state.inputValue.toLowerCase().trim();
                    if (!searchTerm) return options;
                    return options.filter((option) =>
                      option.name.toLowerCase().includes(searchTerm),
                    );
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?.name === value?.name
                  }
                  sx={{ width: "100%" }}
                />
              </Grid>

              {/* Country */}
              <Grid item xs={12} sm={2}>
                <TextField
                  fullWidth
                  label="Country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* ── Line Items Section ── */}
        <Card sx={cardSx}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={sectionHeadingSx}>
                📋 Enquiry Items
              </Typography>
              <Tooltip title="Add Item">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addLineItem}
                  size="small"
                  sx={{ borderRadius: 0 }}
                >
                  Add Item
                </Button>
              </Tooltip>
            </Box>

            {isMobile ? (
              <Box>
                {lineItems.map((item, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      backgroundImage: "none",
                      boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Item #{index + 1}
                      </Typography>
                      {lineItems.length > 1 && (
                        <Tooltip title="Remove Item">
                          <IconButton
                            color="error"
                            onClick={() => removeLineItem(index)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>

                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Autocomplete
                        freeSolo
                        options={voltageOptions.map((o) => o.name)}
                        value={item.voltage_ratio || ""}
                        onChange={(e, val) =>
                          handleLineItemChange(
                            index,
                            "voltage_ratio",
                            val || "",
                          )
                        }
                        onInputChange={(e, val) =>
                          handleLineItemChange(index, "voltage_ratio", val)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Voltage Ratio"
                            size="small"
                            placeholder="e.g. 11000/433"
                            required
                          />
                        )}
                      />

                      <Autocomplete
                        freeSolo
                        options={ratingOptions.map((o) => o.name)}
                        value={item.rating_specification || ""}
                        onChange={(e, val) =>
                          handleLineItemChange(
                            index,
                            "rating_specification",
                            val || "",
                          )
                        }
                        onInputChange={(e, val) =>
                          handleLineItemChange(
                            index,
                            "rating_specification",
                            val,
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Rating Specification"
                            size="small"
                            placeholder="e.g. 500 KVA / Repair detail"
                            required
                          />
                        )}
                      />

                      <Autocomplete
                        freeSolo
                        options={windingOptions.map((o) => o.name)}
                        value={item.winding_material || ""}
                        onChange={(e, val) =>
                          handleLineItemChange(
                            index,
                            "winding_material",
                            val || "",
                          )
                        }
                        onInputChange={(e, val) =>
                          handleLineItemChange(index, "winding_material", val)
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Winding Material"
                            size="small"
                            placeholder="e.g. Copper"
                            required
                          />
                        )}
                      />

                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={item.quantity}
                        onChange={(e) =>
                          handleLineItemChange(
                            index,
                            "quantity",
                            e.target.value,
                          )
                        }
                        required
                        size="small"
                      />

                      <TextField
                        fullWidth
                        label="Est. Price (Optional)"
                        type="number"
                        value={item.price_per_unit}
                        onChange={(e) =>
                          handleLineItemChange(
                            index,
                            "price_per_unit",
                            e.target.value,
                          )
                        }
                        size="small"
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            ) : (
              <TableContainer component={Paper} sx={tablePaperSx}>
                <Table size="small">
                  <TableHead
                    sx={{
                      "& th": {
                        fontWeight: "bold",
                        backgroundColor: `${tableHeadBg} !important`,
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell width="25%">Voltage Ratio *</TableCell>
                      <TableCell width="25%">Rating Spec. *</TableCell>
                      <TableCell width="20%">Winding *</TableCell>
                      <TableCell width="10%">Qty *</TableCell>
                      <TableCell width="15%">Est. Price</TableCell>
                      <TableCell width="5%" align="center">
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={voltageOptions.map((o) => o.name)}
                            value={item.voltage_ratio || ""}
                            onChange={(e, val) =>
                              handleLineItemChange(
                                index,
                                "voltage_ratio",
                                val || "",
                              )
                            }
                            onInputChange={(e, val) =>
                              handleLineItemChange(index, "voltage_ratio", val)
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="e.g. 11000/433"
                                required
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={ratingOptions.map((o) => o.name)}
                            value={item.rating_specification || ""}
                            onChange={(e, val) =>
                              handleLineItemChange(
                                index,
                                "rating_specification",
                                val || "",
                              )
                            }
                            onInputChange={(e, val) =>
                              handleLineItemChange(
                                index,
                                "rating_specification",
                                val,
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="e.g. 500 KVA / Repair detail"
                                required
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Autocomplete
                            freeSolo
                            options={windingOptions.map((o) => o.name)}
                            value={item.winding_material || ""}
                            onChange={(e, val) =>
                              handleLineItemChange(
                                index,
                                "winding_material",
                                val || "",
                              )
                            }
                            onInputChange={(e, val) =>
                              handleLineItemChange(
                                index,
                                "winding_material",
                                val,
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                placeholder="e.g. Copper"
                                required
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.quantity}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "quantity",
                                e.target.value,
                              )
                            }
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="Optional"
                            value={item.price_per_unit}
                            onChange={(e) =>
                              handleLineItemChange(
                                index,
                                "price_per_unit",
                                e.target.value,
                              )
                            }
                          />
                        </TableCell>
                        <TableCell align="center">
                          {lineItems.length > 1 && (
                            <IconButton
                              color="error"
                              onClick={() => removeLineItem(index)}
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* ── Notes Section ── */}
        <Card sx={cardSx}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={sectionHeadingSx}>
                📝 Notes
              </Typography>
              <Tooltip title="Add Note">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addNote}
                  size="small"
                  sx={{ borderRadius: 0 }}
                >
                  Add Note
                </Button>
              </Tooltip>
            </Box>

            {isMobile ? (
              <Box>
                {notes.map((note, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "background.paper",
                      backgroundImage: "none",
                      boxShadow: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : 1,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: "bold", color: "text.secondary" }}
                      >
                        Note #{index + 1}
                      </Typography>
                      {notes.length > 1 && (
                        <Tooltip title="Remove Note">
                          <IconButton
                            color="error"
                            onClick={() => removeNote(index)}
                            size="small"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      label="Note"
                      value={note.note_text}
                      onChange={(e) => handleNoteChange(index, e.target.value)}
                      placeholder="Enter note here..."
                      size="small"
                    />
                  </Paper>
                ))}
              </Box>
            ) : (
              <Box>
                {notes.map((note, index) => (
                  <Box key={index} sx={{ mb: 2, position: "relative" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: "bold",
                          color: "text.secondary",
                          mr: 2,
                        }}
                      >
                        Note {index + 1}:
                      </Typography>
                      {notes.length > 1 && (
                        <Tooltip title="Remove Note">
                          <IconButton
                            color="error"
                            onClick={() => removeNote(index)}
                            size="small"
                            sx={{
                              ml: "auto",
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={4}
                      placeholder="Enter note here..."
                      value={note.note_text}
                      onChange={(e) => handleNoteChange(index, e.target.value)}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: noteBg,
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* ── Enquiry Details Section ── */}
        <Card sx={cardSx}>
          <CardContent>
            <Typography variant="h6" sx={{ ...sectionHeadingSx, mb: 2 }}>
              ⚙️ Enquiry Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enquiry Type"
                  name="enquiry_source"
                  value={formData.enquiry_source || ""}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      enquiry_source: e.target.value,
                    }))
                  }
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Follow Up Date"
                  name="follow_up_date"
                  value={formData.follow_up_date}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Enquiry Status"
                  name="status"
                  value={formData.status}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: e.target.value,
                    }))
                  }
                  required
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </form>
    </Container>
  );
}
